import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, BookOpen, Phone, Mail, Award, GraduationCap } from "lucide-react";

const faculty = [
  {
    name: "Er. Shrihari Poojary",
    title: "Director",
    department: "Mathematics Department",
    phone: "9867913757",
    initials: "SP",
    subjects: ["Calculus", "Algebra", "Coordinate Geometry", "Trigonometry"],
    description:
      "Er. Shrihari Poojary is an engineering graduate and experienced Mathematics educator. Known for his problem-solving approach, he guides students through the most challenging mathematical concepts with clarity and precision. His students consistently excel in JEE and MHT-CET Mathematics.",
    expertise: ["JEE Mathematics", "MHT-CET Mathematics", "HSC Board Maths", "Engineering Mathematics"],
  },
  {
    name: "Dr. Sunny Singh",
    title: "Director",
    department: "Biology Department",
    phone: "7977526163",
    initials: "SS",
    subjects: ["Botany", "Zoology", "Human Physiology", "Genetics"],
    description:
      "Dr. Sunny Singh is a Biology specialist with a doctoral degree, dedicated to making Biology engaging and comprehensible. His deep knowledge of Botany and Zoology, combined with innovative teaching techniques, helps NEET aspirants achieve top scores in Biology.",
    expertise: ["NEET Biology", "HSC Board Biology", "MHT-CET Biology", "Human Physiology"],
  },
  {
    name: "Dr. Sunil Choudhary",
    title: "Director",
    department: "Chemistry Department",
    phone: "7977678307",
    initials: "SC",
    subjects: ["Physical Chemistry", "Organic Chemistry", "Inorganic Chemistry"],
    description:
      "Dr. Sunil Choudhary is a highly experienced Chemistry educator with a doctoral degree. He specializes in breaking down complex chemical concepts into easy-to-understand lessons. His expertise covers the entire Chemistry syllabus for HSC, NEET, JEE, and MHT-CET.",
    expertise: ["NEET Chemistry", "JEE Chemistry", "HSC Board Chemistry", "MHT-CET Chemistry"],
  },
];

export default function Faculty() {
  return (
    <div data-testid="faculty-page" className="pt-16 md:pt-20">
      {/* Header */}
      <section className="page-header py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: "#F2C94C" }}>
            Meet the Experts
          </p>
          <h1
            className="text-4xl md:text-6xl font-extrabold text-white mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Our Faculty
          </h1>
          <p className="text-blue-200 text-base md:text-lg max-w-2xl mx-auto">
            Learn from three expert directors who bring deep subject expertise and years of teaching experience.
          </p>
          <nav className="flex items-center justify-center gap-2 mt-6 text-xs text-blue-300">
            <Link to="/" className="hover:text-yellow-400 transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-yellow-400">Faculty</span>
          </nav>
        </div>
      </section>

      {/* Faculty Cards */}
      <section data-testid="faculty-cards-section" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">
            {faculty.map((f, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100"
                data-testid={`faculty-card-${i}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3">
                  {/* Left Profile */}
                  <div
                    className="p-8 flex flex-col items-center text-center"
                    style={{ backgroundColor: "#0A2540" }}
                  >
                    <div
                      className="w-28 h-28 rounded-full flex items-center justify-center text-3xl font-extrabold mb-4 border-4"
                      style={{
                        backgroundColor: "#F2C94C",
                        color: "#0A2540",
                        borderColor: "rgba(255,255,255,0.2)",
                        fontFamily: "Manrope, sans-serif",
                      }}
                    >
                      {f.initials}
                    </div>
                    <h2
                      className="text-xl font-extrabold text-white mb-1"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {f.name}
                    </h2>
                    <p className="text-sm text-blue-200 mb-1">{f.title}</p>
                    <span
                      className="text-xs font-bold px-3 py-1 rounded-full mb-5"
                      style={{ backgroundColor: "#F2C94C", color: "#0A2540" }}
                    >
                      {f.department}
                    </span>
                    <div className="space-y-2 w-full">
                      <a
                        href={`tel:${f.phone}`}
                        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors"
                        data-testid={`faculty-phone-${i}`}
                      >
                        <Phone size={14} />
                        +91 {f.phone}
                      </a>
                      <a
                        href="mailto:tridentacademy101@gmail.com"
                        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors"
                      >
                        <Mail size={14} />
                        Contact
                      </a>
                    </div>
                  </div>

                  {/* Right Details */}
                  <div className="lg:col-span-2 p-8">
                    <div className="mb-5">
                      <div className="flex items-center gap-2 mb-3">
                        <GraduationCap size={18} color="#F2C94C" />
                        <h3
                          className="font-bold text-base"
                          style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                        >
                          About {f.name}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {/* Subjects */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <BookOpen size={16} color="#F2C94C" />
                          <h4
                            className="font-bold text-sm"
                            style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                          >
                            Subjects
                          </h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {f.subjects.map((s, j) => (
                            <span
                              key={j}
                              className="px-3 py-1 rounded-full text-xs font-semibold"
                              style={{ backgroundColor: "#f0f7ff", color: "#0A2540" }}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Expertise */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Award size={16} color="#F2C94C" />
                          <h4
                            className="font-bold text-sm"
                            style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                          >
                            Teaching Expertise
                          </h4>
                        </div>
                        <div className="space-y-1.5">
                          {f.expertise.map((e, j) => (
                            <div key={j} className="flex items-center gap-2 text-sm text-gray-500">
                              <div
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: "#F2C94C" }}
                              />
                              {e}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-14"
        style={{ background: "linear-gradient(135deg, #0A2540, #0d3362)" }}
      >
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2
            className="text-2xl md:text-3xl font-extrabold text-white mb-3"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Learn from the <span style={{ color: "#F2C94C" }}>Best Faculty</span>
          </h2>
          <p className="text-blue-200 text-sm mb-6">
            Our directors are dedicated to your success. Book a free session to meet them personally.
          </p>
          <Link
            to="/contact"
            data-testid="faculty-cta-btn"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-sm transition-all hover:opacity-90"
            style={{ backgroundColor: "#F2C94C", color: "#0A2540" }}
          >
            Book a Free Session <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
